import { TenantOrderLine } from '~/lib/model';
import { Big } from 'big.js';
import { schema_latest_version, ZodVersionedMetadata } from '~/lib/zod';
import MlmJob from '../../model/MlmJob';
import MlmBrochuresConfig, {
  MlmBrochureShipping,
  MlmBrochuresOption,
  MlmBrochuresQuantity
} from './MlmBrochuresConfig';

export const MLM_BROCHURES_SHIPPING_STANDARD = 22;
export const MLM_BROCHURES_SHIPPING_RUSH = 60;

interface MlmBrochuresOptions {
  revenue: Record<MlmBrochuresQuantity, number>;
  pages: number;
  width: number;
  height: number;
}

export const BROCHURES_OPTIONS: Record<MlmBrochuresOption, MlmBrochuresOptions> = {
  [MlmBrochuresOption.TWO]: {
    revenue: {
      [MlmBrochuresQuantity.QTY_25]: 95,
      [MlmBrochuresQuantity.QTY_50]: 125,
      [MlmBrochuresQuantity.QTY_100]: 175,
      [MlmBrochuresQuantity.QTY_250]: 225,
    },
    pages: 2,
    width: 8.5,
    height: 11,
  },
  [MlmBrochuresOption.FOUR]: {
    revenue: {
      [MlmBrochuresQuantity.QTY_25]: 105,
      [MlmBrochuresQuantity.QTY_50]: 160,
      [MlmBrochuresQuantity.QTY_100]: 200,
      [MlmBrochuresQuantity.QTY_250]: 300,
    },
    pages: 4,
    width: 11,
    height: 17,
  },
  [MlmBrochuresOption.EIGHT]: {
    revenue: {
      [MlmBrochuresQuantity.QTY_25]: 170,
      [MlmBrochuresQuantity.QTY_50]: 260,
      [MlmBrochuresQuantity.QTY_100]: 360,
      [MlmBrochuresQuantity.QTY_250]: 525,
    },
    pages: 8,
    width: 11,
    height: 17,
  },
  [MlmBrochuresOption.TWELVE]: {
    revenue: {
      [MlmBrochuresQuantity.QTY_25]: 225,
      [MlmBrochuresQuantity.QTY_50]: 300,
      [MlmBrochuresQuantity.QTY_100]: 500,
      [MlmBrochuresQuantity.QTY_250]: 750,
    },
    pages: 12,
    width: 11,
    height: 17,
  },
  [MlmBrochuresOption.SIXTEEN]: {
    revenue: {
      [MlmBrochuresQuantity.QTY_25]: 300,
      [MlmBrochuresQuantity.QTY_50]: 360,
      [MlmBrochuresQuantity.QTY_100]: 640,
      [MlmBrochuresQuantity.QTY_250]: 850,
    },
    pages: 16,
    width: 11,
    height: 17,
  },
};

export default class MlmBrochuresJob extends MlmJob<typeof MlmBrochuresConfig> {
  get performable() {
    return MlmBrochuresConfig;
  }

  get eligible() {
    return true;
  }

  get configurable() {
    return true;
  }

  defaultValue(): ZodVersionedMetadata<(typeof MlmBrochuresConfig)['schema']> {
    return {
      option: MlmBrochuresOption.TWO,
      version: schema_latest_version(MlmBrochuresConfig.schema),
      quantity: MlmBrochuresQuantity.QTY_25,
    };
  }

  revenueLines(): TenantOrderLine[] {
    const lines = super.revenueLines();

    lines.push({
      amount: new Big(BROCHURES_OPTIONS[this.metadata.option].revenue[this.metadata.quantity]),
      description: this.performable.name,
      discountable: false,
      taxable: true,
      phase: 'completed',
      id: this.performable.id,
    });

    switch (this.metadata.shipping) {
      case MlmBrochureShipping.STANDARD:
        lines.push({
          amount: new Big(MLM_BROCHURES_SHIPPING_STANDARD),
          description: 'Standard Shipping',
          phase: 'completed',
          discountable: false,
          taxable: true,
          id: 'standard',
        });
        break;
      case MlmBrochureShipping.RUSH:
        lines.push({
          amount: new Big(MLM_BROCHURES_SHIPPING_RUSH),
          description: 'Rush Shipping',
          phase: 'completed',
          discountable: false,
          taxable: true,
          id: 'rush',
        });
        break;
      default:
        break;
    }

    return lines;
  }
}
